import { useTeamList } from "@/hooks/queryHooks/teamHooks";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import { useTenantStore } from "@/stores/tenantStore";
import { useUserStore } from "@/stores/userStore";
import type { Approver } from "@repo/types";
import { InputInfo } from "@repo/ui/components/Base/InputInfo";
import { Label } from "@repo/ui/components/Base/Label";
import { RadioInput } from "@repo/ui/components/Base/RadioInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { useFormikContext } from "formik";
import { useMemo } from "react";

interface FormikSelectApproverProps {
  name: string;
  value: Approver | undefined;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
}

export const FormikSelectApprover = ({
  name,
  value,
  disabled,
  error,
  errorMessage,
}: FormikSelectApproverProps) => {
  const tenantId = useTenantStore((s) => s.id);
  const team = useUserStore((s) => s.activeTeam);
  const { setFieldValue } = useFormikContext();
  const { data: teamsData } = useTeamList(tenantId);
  const { data: usersData } = useUserList(tenantId);

  const filteredUsers = useMemo(
    () =>
      usersData?.filter(
        (user) =>
          user.role !== "none" ||
          (team &&
            team !== "org" &&
            team.teamMembers.some(
              (member) => member.userId === user.id && member.role !== "none"
            ))
      ),
    [usersData, team]
  );

  return (
    <div className="flex flex-col gap-4 w-full">
      <Label label="Approver Type" />

      <RadioInput
        disabled={disabled}
        label="Team"
        orientation="horizontal"
        containerStyle="justify-end flex-row-reverse ml-2 mt-0"
        labelStyle="min-w-32 [&+div]:w-auto"
        checked={value?.approverType === "team"}
        onCheckedChange={(checked) => {
          if (checked)
            setFieldValue(name, { approverId: "", approverType: "team" });
        }}
      />
      <RadioInput
        disabled={disabled}
        label="User"
        orientation="horizontal"
        containerStyle="justify-end flex-row-reverse ml-2 mt-0"
        labelStyle="min-w-32 [&+div]:w-auto"
        checked={value?.approverType === "user"}
        onCheckedChange={(checked) => {
          if (checked)
            setFieldValue(name, { approverId: "", approverType: "user" });
        }}
      />
      <RadioInput
        disabled={disabled}
        label="No Approval Required"
        orientation="horizontal"
        containerStyle="justify-end flex-row-reverse ml-2 mt-0"
        labelStyle="min-w-32 [&+div]:w-auto"
        checked={value?.approverType === "none"}
        onCheckedChange={(checked) => {
          if (checked)
            setFieldValue(name, { approverId: "", approverType: "none" });
        }}
      />

      {/* <SelectInput
        disabled={disabled}
        label="Approver Type"
        value={value?.approverType}
        options={[
          <SelectItem value="team" key="team">
            Team
          </SelectItem>,
          <SelectItem value="user" key="user">
            User
          </SelectItem>,
          <SelectItem value="none" key="none">
            No Approval Required
          </SelectItem>,
        ]}
        onValueChange={(approverType) =>
          setFieldValue(name, { approverId: "", approverType })
        }
      /> */}
      {value?.approverType && value.approverType !== "none" && (
        <SelectInput
          disabled={disabled}
          label={value.approverType === "team" ? "Approving Team" : "Approver"}
          value={value.approverId}
          options={
            value.approverType === "team"
              ? teamsData?.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                )) ?? []
              : filteredUsers?.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {`${user.firstName} ${user.lastName}`}
                  </SelectItem>
                )) ?? []
          }
          onValueChange={(idValue) =>
            setFieldValue(name, { ...value, approverId: idValue })
          }
        />
      )}
      {error && errorMessage && <InputInfo info={errorMessage} />}
    </div>
  );
};
